import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Image as ImageIcon, Video, Mic, X, Check, Clock } from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";
import { TopStoryButton, WallToggle } from "../../components/memories/TopStoryControls";
import { NewspaperButtons } from "../../components/memories/NewspaperButtons";
import { RecapCard } from "../../components/planning/PlanningCards";
import { WatermarkCard } from "../../components/memories/WatermarkCard";

export default function MemoriesPage() {
  const [wedding, setWedding] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  async function load() {
    const w = await api.get("/weddings/mine");
    setWedding(w.data);
    const m = await api.get(`/weddings/${w.data.wedding_id}/memories`);
    setItems(m.data);
  }
  useEffect(() => { load().catch(() => {}); }, []);

  async function onPick(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("caption", "");
        fd.append("event_category", "wedding_day");
        await api.post(`/weddings/${wedding.wedding_id}/memories`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`${files.length} memorie${files.length > 1 ? "s" : ""} uploaded`);
      e.target.value = "";
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setUploading(false); }
  }

  async function toggleApprove(it) {
    await api.patch(`/memories/${it.item_id}?approved=${!it.approved}`);
    load();
  }

  const filtered = items.filter((it) => {
    if (filter === "all") return true;
    if (filter === "pending") return !it.approved;
    if (filter === "guest") return it.event_category === "guest_upload";
    return it.event_category === filter;
  });

  return (
    <div data-testid="memories-page">
      <RecapCard />
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-script text-[var(--gold)] text-3xl">Forever Memory Vault</p>
          <h1 className="font-serif font-light text-4xl">Every moment. Preserved.</h1>
        </div>
        <button onClick={() => fileRef.current?.click()} disabled={uploading || !wedding} data-testid="upload-memory-btn" className="btn-gold">
          <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload Memories"}
        </button>
        <input data-testid="memory-file-input" ref={fileRef} onChange={onPick} type="file" multiple accept="image/*,video/*,audio/*" className="hidden" />
      </div>

      <NewspaperButtons />
      <WatermarkCard />

      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { v: "all", l: "All" },
          { v: "pending", l: "Pending Approval" },
          { v: "wedding_day", l: "Wedding Day" },
          { v: "engagement", l: "Engagement" },
          { v: "honeymoon", l: "Honeymoon" },
          { v: "guest", l: "Guest Uploads" },
        ].map((t) => (
          <button key={t.v} onClick={() => setFilter(t.v)} className={`text-xs uppercase tracking-wider px-4 py-2 rounded-full transition ${filter === t.v ? "bg-[var(--ink)] text-white" : "border border-[var(--line)] text-[var(--muted)] hover:border-[var(--gold)]"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center bg-white border border-[var(--line)] rounded-2xl py-16 px-6">
          <ImageIcon className="w-10 h-10 mx-auto text-[var(--gold)] mb-4" />
          <p className="font-serif text-2xl mb-2">Your forever archive starts here</p>
          <p className="text-[var(--muted)] max-w-md mx-auto mb-6">Upload engagement photos, voice messages, proposal videos and wedding day moments. Guests can also upload via your QR code.</p>
          <button onClick={() => fileRef.current?.click()} className="btn-gold"><Upload className="w-4 h-4" /> Upload Your First Memory</button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((it) => (
            <div key={it.item_id} className="group relative bg-white border border-[var(--line)] rounded-xl overflow-hidden">
              <div className="aspect-square bg-[var(--bg-warm)] relative">
                {it.content_type?.startsWith("image/") ? (
                  <img src={`${BACKEND_URL}/api/files/${it.storage_path}`} alt={it.caption} className="w-full h-full object-cover" />
                ) : it.content_type?.startsWith("video/") ? (
                  <video controls src={`${BACKEND_URL}/api/files/${it.storage_path}`} className="w-full h-full object-cover" />
                ) : it.content_type?.startsWith("audio/") ? (
                  <div className="w-full h-full flex items-center justify-center"><Mic className="w-10 h-10 text-[var(--gold)]" /></div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[var(--muted)] text-xs p-2">{it.content_type}</div>
                )}
              </div>
              {it.content_type?.startsWith("audio/") && (
                <audio controls src={`${BACKEND_URL}/api/files/${it.storage_path}`} className="w-full" />
              )}
              <div className="p-3 flex items-center justify-between gap-2">
                <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] flex items-center gap-1">
                  {it.event_category === "guest_upload" && <span>Guest · {it.uploaded_by_name}</span>}
                  {it.event_category && it.event_category !== "guest_upload" && <span>{it.event_category.replace("_", " ")}</span>}
                </div>
                <div className="flex items-center gap-1">
                {it.content_type?.startsWith("image/") && <TopStoryButton it={it} pinned={wedding?.top_story?.item_id === it.item_id} onDone={load} />}
                <WallToggle it={it} onDone={load} />
                <button onClick={() => toggleApprove(it)} title={it.approved ? "Approved" : "Pending"} className={`p-1 rounded-full ${it.approved ? "bg-[var(--gold)] text-white" : "bg-[var(--bg-warm)] text-[var(--muted)]"}`}>
                  {it.approved ? <Check className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
